import { useEffect, useState } from 'react';
import { User, Users, ArrowRightToLine, ArrowLeftToLine } from 'lucide-react';
import { FormInput } from '@/components/common/FormInput';
import { FormDatePicker } from '@/components/common/FormDatePicker';
import { FormTextarea } from '@/components/common/FormTextarea';
import { PrimaryButton } from '@/components/common/PrimaryButton';
import CsvUpload from '@/components/common/CsvUpload';
import { useStep } from '@/context/StepProvider';
import { toast } from 'react-toastify';
import {
  useBulkUploadCertificatesMutation,
  useCreateCertificateMutation,
} from '@/store/api/certificateApi';
import { bulkCertificateSchema, singleCertificateSchema } from '@/utils/validation';
import { useLocation, useNavigate } from 'react-router-dom';

export default function CreateCertificatePage() {
  const { step, setStep } = useStep();
  const [tab, setTab] = useState<'single' | 'bulk'>('single');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [createCertificate, { isLoading: createLoading }] = useCreateCertificateMutation();
  const [bulkUploadCertificates, { isLoading: bulkLoading }] = useBulkUploadCertificatesMutation();
  const location = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = useState<{
    title: string;
    issuingAuthority: string;
    date: Date | null;
    description: string;
    eventId: string;
    level: string;

    candidates: {
      name: string;
      email: string;
      walletAddress: string;
      type: string;
    }[];

    bulkCount: number;
    generatePlaceholders: boolean;
  }>({
    title: '',
    issuingAuthority: '',
    date: null,
    description: '',
    eventId: '',
    level: '',
    candidates: [
      {
        name: '',
        email: '',
        walletAddress: '',
        type: '',
      },
    ],

    bulkCount: 0,
    generatePlaceholders: false,
  });
  const eventIdFromState = (location.state as any)?.eventId;

  const isSubmitting = createLoading || bulkLoading;
  useEffect(() => {
    if (eventIdFromState) {
      setForm((prev) => ({
        ...prev,
        eventId: eventIdFromState,
      }));
    }
  }, [eventIdFromState]);

  useEffect(() => {
    setStep(1);
  }, []);
  const validateForm = (tab: 'single' | 'bulk') => {
    if (tab === 'single') {
      return singleCertificateSchema.safeParse(form);
    } else {
      return bulkCertificateSchema.safeParse({
        ...form,
        file: selectedFile,
      });
    }
  };
  const handleChange = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    // setErrors((prev) => ({ ...prev, [key]: '' }));
    setErrors((prev) => {
      const newErrors = { ...prev };

      delete newErrors[key];

      return newErrors;
    });
  };

  const handleCandidateChange = (
    index: number,
    key: keyof (typeof form.candidates)[0],
    value: string
  ) => {
    const updatedCandidates = [...form.candidates];

    updatedCandidates[index] = {
      ...updatedCandidates[index],
      [key]: value,
    } as (typeof form.candidates)[0];

    setForm((prev) => ({
      ...prev,
      candidates: updatedCandidates,
    }));

    setErrors((prev) => {
      const newErrors = { ...prev };

      delete newErrors[`candidates.${index}.${key}`];

      return newErrors;
    });
  };
  const addCandidate = () => {
    setForm((prev) => ({
      ...prev,
      candidates: [
        ...prev.candidates,
        {
          name: '',
          email: '',
          walletAddress: '',
          type: '',
        },
      ],
    }));
  };
  const removeCandidate = (index: number) => {
    if (form.candidates.length === 1) return;

    const updated = form.candidates.filter((_, i) => i !== index);

    setForm((prev) => ({
      ...prev,
      candidates: updated,
    }));
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    try {
      const result = validateForm(tab);

      if (!result.success) {
        const fieldErrors: Record<string, string> = {};
        result.error.errors.forEach((err) => {
          fieldErrors[err.path.join('.')] = err.message;
        });
        setErrors(fieldErrors);
        return;
      }

      setErrors({});

      if (tab === 'bulk') {
        const formData = new FormData();

        if (selectedFile) {
          formData.append('file', selectedFile);
        }

        formData.append('title', form.title);
        formData.append('issuingAuthority', form.issuingAuthority);
        formData.append('level', form.level);
        formData.append('description', form.description);
        formData.append('issuedAt', form.date ? new Date(form.date).toISOString() : '');
        formData.append('bulkCount', String(form.bulkCount || 0));
        formData.append('eventId', form.eventId);
        await bulkUploadCertificates(formData).unwrap();

        toast.success('Bulk certificates uploaded successfully');
        setStep(1);
        navigate('/certificate-list');
        return;
      }

      await createCertificate({
        title: form.title,
        issuingAuthority: form.issuingAuthority,
        issuedAt: form.date ? new Date(form.date).toISOString() : '',
        description: form.description,
        level: form.level,
        eventId: form.eventId,

        candidates: form.candidates.map((candidate) => ({
          name: candidate.name,
          email: candidate.email || undefined,
          walletAddress: candidate.walletAddress,
          type: candidate.type,
        })),
      }).unwrap();

      toast.success('Certificate created successfully');
      setStep(1);
      navigate('/certificate-list');
    } catch (err) {
      toast.error((err as any)?.data?.message || 'Something went wrong');
    }
  };
  const validateStep1 = () => {
    const step1Schema = singleCertificateSchema.pick({
      title: true,
      issuingAuthority: true,
      date: true,
      description: true,
      level: true,
    });

    const result = step1Schema.safeParse(form);

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};

      result.error.errors.forEach((err) => {
        fieldErrors[err.path.join('.')] = err.message;
      });

      setErrors(fieldErrors);
      return false;
    }

    setErrors({});
    return true;
  };
  return (
    <div className="bg-black text-white  lg:p-6">
      {step === 1 && (
        <div className="bg-[#0b0b0b] border border-white/10 rounded-xl p-2 lg:p-6 max-w-[1480px] mx-auto shadow-lg">
          <h2 className="font-24 font-bold mb-1">Certificate Details</h2>
          <p className="font-18 text-[#A7A7A7] mb-6">
            Enter the basic information for the certificate
          </p>

          <div className="lg:grid  grid-cols-2 space-y-4 lg:space-y-0 gap-4">
            <div>
              <FormInput
                label="Certificate Title"
                required
                placeholder="Enter certificate title"
                value={form.title}
                onChange={(e) => handleChange('title', e.target.value)}
              />
              {errors.title && <p className="text-sm text-red-500">{errors.title}</p>}
            </div>

            <div>
              <FormDatePicker
                label="Date"
                value={form.date}
                onChange={(date) => handleChange('date', date)}
              />
              {errors.date && <p className="text-sm text-red-500">{errors.date}</p>}
            </div>

            <div>
              <FormInput
                label="Issued By / Organization"
                required
                placeholder="Enter organization"
                value={form.issuingAuthority}
                onChange={(e) => handleChange('issuingAuthority', e.target.value)}
              />
              {errors.issuingAuthority && (
                <p className="text-sm text-red-500">{errors.issuingAuthority}</p>
              )}
            </div>
            <div className="flex flex-col gap-1 w-full">
              <label className="font-18 font-medium text-white">
                Level <span className="text-[#FF0000]">*</span>
              </label>

              <select
                value={form.level}
                onChange={(e) => handleChange('level', e.target.value)}
                className="bg-transparent border border-white/20 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
              >
                <option value="" className="bg-[#0b0b0f] text-gray-400">
                  Select Level
                </option>

                <option value="Beginner" className="bg-[#0b0b0f] text-white">
                  Beginner
                </option>

                <option value="Intermediate" className="bg-[#0b0b0f] text-white">
                  Intermediate
                </option>

                <option value="Expert" className="bg-[#0b0b0f] text-white">
                  Expert
                </option>
              </select>

              {errors.level && <p className="text-sm text-red-500">{errors.level}</p>}
            </div>

            <div className="col-span-2">
              <FormTextarea
                label="Reason / Description"
                required
                placeholder="Enter reason / description"
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
              />
              {errors.description && <p className="text-sm text-red-500">{errors.description}</p>}
            </div>
          </div>

          <div className="flex justify-end gap-4 mt-6">
            <button
              onClick={() => navigate(-1)}
              className="border border-white/20 px-4 py-2 rounded-md text-sm hover:bg-white/10 flex items-center gap-2"
            >
              <ArrowLeftToLine /> Back
            </button>

            <PrimaryButton
              onClick={() => {
                const isValid = validateStep1();

                if (isValid) {
                  setStep(2);
                } else {
                  // toast.error('Please fill all required fields');
                }
              }}
            >
              <span className="flex items-center gap-2">
                Next <ArrowRightToLine />
              </span>
            </PrimaryButton>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="bg-[#0b0b0b] border border-white/10 rounded-xl p-6 max-w-[1480px] mx-auto shadow-lg">
          <h2 className="font-24 font-bold mb-1">Add Candidates</h2>
          <p className="font-18 text-[#A7A7A7] mb-6">
            Choose how you want to add certificate recipients
          </p>

          <div className="flex gap-6  border-white/10 mb-6">
            <button
              onClick={() => setTab('single')}
              className={`pb-2 flex items-center gap-2 font-16 ${
                tab === 'single'
                  ? 'border-b-2  border-white bg-[linear-gradient(178.66deg,#27C8F6_1%,#6F72FF_89.83%)] text-white p-2 rounded-t-lg'
                  : 'text-gray-400'
              }`}
            >
              <User size={18} /> Single Entry
            </button>

            <button
              onClick={() => setTab('bulk')}
              className={`pb-2 flex items-center gap-2 font-16 ${
                tab === 'bulk'
                  ? 'border-b-2  border-white bg-[linear-gradient(178.66deg,#27C8F6_1%,#6F72FF_89.83%)] text-white p-2 rounded-t-lg'
                  : 'text-gray-400'
              }`}
            >
              <Users size={18} /> Bulk Entry
            </button>
          </div>

          {tab === 'single' && (
            <div>
              {form.candidates.map((candidate, index) => (
                <div key={index} className="border border-white/10 rounded-xl p-4 mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Candidate {index + 1}</h3>

                    {form.candidates.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCandidate(index)}
                        className="text-red-500 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="lg:grid grid-cols-2 gap-4">
                    {/* NAME */}
                    <div>
                      <FormInput
                        label="Candidate Name"
                        required
                        placeholder="Enter candidate name"
                        value={candidate.name}
                        onChange={(e) => handleCandidateChange(index, 'name', e.target.value)}
                      />

                      {errors[`candidates.${index}.name`] && (
                        <p className="text-sm text-red-500">{errors[`candidates.${index}.name`]}</p>
                      )}
                    </div>

                    {/* EMAIL */}
                    <div>
                      <FormInput
                        label="Email (Optional)"
                        placeholder="Enter email"
                        value={candidate.email}
                        onChange={(e) => handleCandidateChange(index, 'email', e.target.value)}
                      />
                    </div>

                    {/* TYPE */}
                    <div className="flex flex-col gap-1">
                      <label className="font-18 text-white">
                        Certificate Type <span className="text-[#ff0000]">*</span>
                      </label>

                      <select
                        value={candidate.type}
                        onChange={(e) => handleCandidateChange(index, 'type', e.target.value)}
                        className="bg-transparent border border-white/20 rounded-md px-3 py-2 text-sm text-white"
                      >
                        <option value="" className="bg-black">
                          Select Certificate Type
                        </option>

                        <option className="bg-black">Participation</option>

                        <option className="bg-black">Appreciation</option>

                        <option className="bg-black">Training</option>

                        <option className="bg-black">Achievement</option>
                      </select>

                      {errors[`candidates.${index}.type`] && (
                        <p className="text-sm text-red-500">{errors[`candidates.${index}.type`]}</p>
                      )}
                    </div>

                    {/* WALLET */}
                    <div>
                      <FormInput
                        label="Wallet Address"
                        value={candidate.walletAddress}
                        onChange={(e) =>
                          handleCandidateChange(index, 'walletAddress', e.target.value)
                        }
                      />

                      {errors[`candidates.${index}.walletAddress`] && (
                        <p className="text-sm text-red-500">
                          {errors[`candidates.${index}.walletAddress`]}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* ADD BUTTON */}
              <button
                type="button"
                onClick={addCandidate}
                className="border border-white/20 px-4 py-2 rounded-md hover:bg-white/10"
              >
                + Add Candidate
              </button>
            </div>
          )}

          {tab === 'bulk' && (
            <div className="lg:grid grid-cols-2 gap-4 space-y-4 lg:space-y-0 items-center">
              <div>
                <FormInput
                  label="Number of Certificates"
                  type="number"
                  value={String(form.bulkCount)}
                  onChange={(e) => handleChange('bulkCount', Number(e.target.value))}
                />
                {errors.bulkCount && <p className="text-sm text-red-500">{errors.bulkCount}</p>}
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm pt-0 lg:pt-5 ">
                  <input
                    type="checkbox"
                    checked={form.generatePlaceholders}
                    onChange={(e) => handleChange('generatePlaceholders', e.target.checked)}
                    className="accent-blue-500 w-4 h-4 "
                  />
                  Generate placeholders
                </label>
                {errors.generatePlaceholders && (
                  <p className="text-sm text-red-500">{errors.generatePlaceholders}</p>
                )}
              </div>
              <div className="col-span-2">
                <CsvUpload
                  onChange={(file) => {
                    setSelectedFile(file);

                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.file;
                      return newErrors;
                    });
                  }}
                />
                {errors.file && <p className="text-red-500 text-sm">{errors.file}</p>}
              </div>
            </div>
          )}

          <div className="flex gap-6 justify-end mt-6">
            <button
              onClick={() => setStep(1)}
              className="border border-white/20 px-4 py-2 rounded-md text-sm hover:bg-white/10 flex items-center gap-2"
            >
              <ArrowLeftToLine /> Back
            </button>

            <PrimaryButton onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <span
                  className={`flex items-center gap-2  ${isSubmitting ? 'cursor-not-allowed' : ''}`}
                >
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  {bulkLoading ? 'Uploading...' : 'Generating...'}
                </span>
              ) : (
                <span className="flex items-center gap-2">Generate</span>
              )}
            </PrimaryButton>
          </div>
        </div>
      )}
    </div>
  );
}
